<template>
    <section class="c-compare">
        <div class="c-compare__wrapper" ref="$wrapper">
            <div class="c-compare__sticky">
                <div class="preview__media">
                    <div class="divider" ref="$devider">
                        <div class="circle">
                            <SvgsArrowChevron />
                        </div>
                    </div>
                    <div class="preview__media__left" ref="$lMedia">
                        <div class="heading h3 pst-100 psl-20 psl-md-60 pst-md-100">{{ data.lHeading }}</div>
                        <div class="media">
                            <video
                                :poster="data.lMedia[0].webp"
                                autoplay
                                playsinline
                                loop
                                muted
                                preload="none"
                                class="video__bg"
                                ref="$lVideo"
                            >
                                <source :src="data.lVideo[0].url" type="video/mp4" />
                                Your browser doesn't support HTML5 video tag.
                            </video>
                        </div>
                        <div class="description msb-20 msl-20 msb-md-60 msl-md-60" v-html="nl2br(data.lDesc)"></div>
                    </div>
                    <div class="preview__media__right" ref="$rMedia">
                        <div class="heading h3 psr-20 pst-100 psr-md-60 pst-md-100">{{ data.rHeading }}</div>
                        <div class="media">
                            <video
                                :poster="data.rMedia[0].webp"
                                loop
                                muted
                                preload="none"
                                class="video__bg"
                                ref="$rVideo"
                            >
                                <source :src="data.rVideo[0].url" type="video/mp4" />
                                Your browser doesn't support HTML5 video tag.
                            </video>
                        </div>
                        <div class="description msr-20 msb-20 msb-md-60 msr-md-60" v-html="nl2br(data.rDesc)"></div>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup>
    const props = defineProps({
        data: {
            type: Object,
            default: {}
        }
    });

    const { $gsap: gsap } = useNuxtApp();

    const $wrapper = ref(null);
    const $lMedia = ref(null);
    const $rMedia = ref(null);
    const $lVideo = ref(null);
    const $rVideo = ref(null);
    const $devider = ref(null);

    useStartAnimations(() => {
        const matchMedia = gsap.matchMedia();

        matchMedia.add(
            "(min-width: 768px)",
            (context) => {
                const lDesc = $lMedia.value.querySelector(".description");
                const rDesc = $rMedia.value.querySelector(".description");
                const lHeading = $lMedia.value.querySelector(".heading");
                const rHeading = $rMedia.value.querySelector(".heading");

                gsap.set(rDesc, { opacity: 0, yPercent: 100 });
                gsap.set(rHeading, { opacity: 0, yPercent: -20 });

                gsap.set($lMedia.value, { xPercent: 0 });
                gsap.set($rMedia.value, { xPercent: 60 });
                gsap.set($devider.value, { left: "60%" });

                const wrapperTimeline = gsap.timeline({
                    scrollTrigger: {
                        trigger: $wrapper.value,
                        scrub: true,
                        start: "top -15%",
                        end: "bottom bottom",
                        invalidateOnRefresh: true
                    }
                });

                wrapperTimeline.addLabel("firstScene");
                wrapperTimeline.to(
                    lDesc,
                    {
                        yPercent: 100,
                        opacity: 0,
                        duration: 0.5,
                        ease: "power2"
                    },
                    "firstScene"
                );
                wrapperTimeline.to(
                    lHeading,
                    {
                        yPercent: -20,
                        opacity: 0,
                        duration: 0.5,
                        ease: "power2"
                    },
                    "firstScene"
                );

                wrapperTimeline.addLabel("resizeScene");
                wrapperTimeline.to(
                    $devider.value,
                    {
                        left: 0,
                        duration: 0.5,
                        ease: "none",
                        onStart: () => {
                            $lVideo.value.pause();
                            $rVideo.value.play();
                        },
                        onReverseComplete: () => {
                            $lVideo.value.play();
                            $rVideo.value.pause();
                        }
                    },
                    "resizeScene"
                );

                wrapperTimeline.to(
                    $rMedia.value,
                    {
                        xPercent: 0,
                        duration: 0.5,
                        ease: "none"
                    },
                    "resizeScene"
                );

                wrapperTimeline.addLabel("previewScene");
                wrapperTimeline.to(
                    rDesc,
                    {
                        yPercent: 0,
                        opacity: 1,
                        duration: 0.5,
                        ease: "power2"
                    },
                    "previewScene"
                );
                wrapperTimeline.to(
                    rHeading,
                    {
                        yPercent: 0,
                        opacity: 1,
                        duration: 0.5,
                        ease: "power2"
                    },
                    "previewScene"
                );

                return () => {
                    wrapperTimeline.remove();
                };
            },
            $wrapper.value
        );

        matchMedia.add(
            "(max-width: 767px)",
            (context) => {
                const lDesc = $lMedia.value.querySelector(".description");
                const rDesc = $rMedia.value.querySelector(".description");
                const lHeading = $lMedia.value.querySelector(".heading");
                const rHeading = $rMedia.value.querySelector(".heading");

                gsap.set($rMedia.value, { yPercent: 100 });
                gsap.set(rDesc, { opacity: 0, yPercent: 100 });
                gsap.set(rHeading, { opacity: 0, yPercent: -20 });

                const wrapperTimeline = gsap.timeline({
                    scrollTrigger: {
                        trigger: $wrapper.value,
                        scrub: true,
                        start: "top -15%",
                        end: "bottom bottom",
                        invalidateOnRefresh: true,
                        onStart: () => {
                            $lVideo.value.play();
                        }
                    }
                });

                wrapperTimeline.addLabel("firstScene");
                wrapperTimeline.to(
                    lDesc,
                    {
                        yPercent: 100,
                        opacity: 0,
                        duration: 0.5,
                        ease: "power2"
                    },
                    "firstScene"
                );
                wrapperTimeline.to(
                    lHeading,
                    {
                        yPercent: -20,
                        opacity: 0,
                        duration: 0.5,
                        ease: "power2"
                    },
                    "firstScene"
                );

                wrapperTimeline.to($rMedia.value, {
                    yPercent: 0,
                    onReverseComplete: () => {
                        $lVideo.value.play();
                        $rVideo.value.pause();
                    },
                    onComplete: () => {
                        $lVideo.value.pause();
                        $rVideo.value.play();
                    }
                });

                wrapperTimeline.addLabel("previewScene");
                wrapperTimeline.to(
                    rDesc,
                    {
                        yPercent: 0,
                        opacity: 1,
                        duration: 0.5,
                        ease: "power2"
                    },
                    "previewScene"
                );
                wrapperTimeline.to(
                    rHeading,
                    {
                        yPercent: 0,
                        opacity: 1,
                        duration: 0.5,
                        ease: "power2"
                    },
                    "previewScene"
                );
            },
            $wrapper.value
        );
    });
</script>
